import { gql } from '@apollo/client';
import {
    CRM_ADMIN_BLOCK_FIELDS,
    CRM_BOOKING_LINK_FIELDS,
    CRM_CALENDAR_BOOKING_FIELDS,
    CRM_MEETING_TYPE_FIELDS,
    CRM_SCHEDULE_FIELDS
} from './queries';

/* Booking-link Google connection disconnect (POGO-13214 Phase 1) — the new
   capability the Book-a-call connection never had. */
export const DISCONNECT_CRM_BOOKING_GOOGLE = gql`
    mutation DisconnectCrmBookingGoogle {
        disconnectCrmBookingGoogle
    }
`;

/* Team-member schedule (POGO-13214 Phase 2) — save the current admin's weekly
   working hours + timezone. */
export const SAVE_MY_CRM_SCHEDULE = gql`
    mutation SaveMyCrmSchedule($input: SaveCrmScheduleInput!) {
        saveMyCrmSchedule(input: $input) {
            ${CRM_SCHEDULE_FIELDS}
        }
    }
`;

/* Booking calendar page (POGO-13214) — save a chosen user's schedule. */
export const SAVE_CRM_SCHEDULE_FOR_ADMIN = gql`
    mutation SaveCrmScheduleForAdmin($adminId: ID!, $input: SaveCrmScheduleInput!) {
        saveCrmScheduleForAdmin(adminId: $adminId, input: $input) {
            ${CRM_SCHEDULE_FIELDS}
        }
    }
`;

/* Admin blocks (POGO-13214) — company-wide unavailable periods. */
export const CREATE_CRM_ADMIN_BLOCK = gql`
    mutation CreateCrmAdminBlock($input: CreateCrmAdminBlockInput!) {
        createCrmAdminBlock(input: $input) {
            ${CRM_ADMIN_BLOCK_FIELDS}
        }
    }
`;

export const UPDATE_CRM_ADMIN_BLOCK = gql`
    mutation UpdateCrmAdminBlock($input: UpdateCrmAdminBlockInput!) {
        updateCrmAdminBlock(input: $input) {
            ${CRM_ADMIN_BLOCK_FIELDS}
        }
    }
`;

export const DELETE_CRM_ADMIN_BLOCK = gql`
    mutation DeleteCrmAdminBlock($id: ID!) {
        deleteCrmAdminBlock(id: $id) {
            _id
        }
    }
`;

/* Delete a recurring block and all following occurrences (POGO-13267). */
export const DELETE_CRM_ADMIN_BLOCK_SERIES = gql`
    mutation DeleteCrmAdminBlockSeries($seriesId: String!, $fromStartAt: DateTime!) {
        deleteCrmAdminBlockSeries(seriesId: $seriesId, fromStartAt: $fromStartAt)
    }
`;

/* Meeting types (POGO-13214 Phase 3) — staff CRUD over the object a booking
   link exposes. Archive hides it from the public link without deleting. */
export const CREATE_CRM_MEETING_TYPE = gql`
    mutation CreateCrmMeetingType($input: CreateCrmMeetingTypeInput!) {
        createCrmMeetingType(input: $input) {
            ${CRM_MEETING_TYPE_FIELDS}
        }
    }
`;

export const UPDATE_CRM_MEETING_TYPE = gql`
    mutation UpdateCrmMeetingType($input: UpdateCrmMeetingTypeInput!) {
        updateCrmMeetingType(input: $input) {
            ${CRM_MEETING_TYPE_FIELDS}
        }
    }
`;

export const ARCHIVE_CRM_MEETING_TYPE = gql`
    mutation ArchiveCrmMeetingType($id: ID!) {
        archiveCrmMeetingType(id: $id) {
            ${CRM_MEETING_TYPE_FIELDS}
        }
    }
`;

export const DELETE_CRM_MEETING_TYPE = gql`
    mutation DeleteCrmMeetingType($id: ID!) {
        deleteCrmMeetingType(id: $id) {
            ${CRM_MEETING_TYPE_FIELDS}
        }
    }
`;

/* Booking links (POGO-13214 Phase 4) — staff CRUD over the shareable slug.
   Set status inactive to instantly close the public page. */
export const CREATE_CRM_BOOKING_LINK = gql`
    mutation CreateCrmBookingLink($input: CreateCrmBookingLinkInput!) {
        createCrmBookingLink(input: $input) {
            ${CRM_BOOKING_LINK_FIELDS}
        }
    }
`;

export const UPDATE_CRM_BOOKING_LINK = gql`
    mutation UpdateCrmBookingLink($input: UpdateCrmBookingLinkInput!) {
        updateCrmBookingLink(input: $input) {
            ${CRM_BOOKING_LINK_FIELDS}
        }
    }
`;

export const DELETE_CRM_BOOKING_LINK = gql`
    mutation DeleteCrmBookingLink($id: ID!) {
        deleteCrmBookingLink(id: $id) {
            ${CRM_BOOKING_LINK_FIELDS}
        }
    }
`;

/* Meeting attendance + cancellation (POGO-13214 Phase 8) — mark a booking-link
   meeting Attended / DNA (fires the follow-up automations) or cancel it
   (deletes the Google event and notifies the visitor). */
export const MARK_CRM_MEETING_ATTENDANCE = gql`
    mutation MarkCrmMeetingAttendance($bookingId: ID!, $attendance: CrmCalendarBookingAttendance!) {
        markCrmMeetingAttendance(bookingId: $bookingId, attendance: $attendance) {
            ${CRM_CALENDAR_BOOKING_FIELDS}
        }
    }
`;

export const CANCEL_CRM_MEETING = gql`
    mutation CancelCrmMeeting($bookingId: ID!) {
        cancelCrmMeeting(bookingId: $bookingId) {
            ${CRM_CALENDAR_BOOKING_FIELDS}
        }
    }
`;

/* Cancel a recurring meeting and all following occurrences (POGO-13267). */
export const CANCEL_CRM_MEETING_SERIES = gql`
    mutation CancelCrmMeetingSeries($seriesId: String!, $fromStartAt: DateTime!) {
        cancelCrmMeetingSeries(seriesId: $seriesId, fromStartAt: $fromStartAt)
    }
`;

export const RESCHEDULE_CRM_MEETING = gql`
    mutation RescheduleCrmMeeting($bookingId: ID!, $startAt: DateTime!) {
        rescheduleCrmMeeting(bookingId: $bookingId, startAt: $startAt) {
            ${CRM_CALENDAR_BOOKING_FIELDS}
        }
    }
`;

export const SET_CRM_MEETING_NOTE = gql`
    mutation SetCrmMeetingNote($bookingId: ID!, $note: String) {
        setCrmMeetingNote(bookingId: $bookingId, note: $note) {
            ${CRM_CALENDAR_BOOKING_FIELDS}
        }
    }
`;

/* Edit a meeting's attendees (POGO-13267 Phase 4) — full set-replace of the
   contacts and team members; the Google event is patched and everyone
   added/removed is notified. */
export const UPDATE_CRM_MEETING_ATTENDEES = gql`
    mutation UpdateCrmMeetingAttendees($input: UpdateCrmMeetingAttendeesInput!) {
        updateCrmMeetingAttendees(input: $input) {
            ${CRM_CALENDAR_BOOKING_FIELDS}
        }
    }
`;

/* Replace a meeting whose host or meeting type changed (POGO-13267 Phase 5) —
   cancels the old booking and places a fresh one under the hood, returning the
   NEW booking; attendees receive a cancellation + a fresh invitation. */
export const REPLACE_CRM_MEETING = gql`
    mutation ReplaceCrmMeeting($input: ReplaceCrmMeetingInput!) {
        replaceCrmMeeting(input: $input) {
            ${CRM_CALENDAR_BOOKING_FIELDS}
        }
    }
`;

/* Set the calendar's visible day window (POGO-13214). */
export const SAVE_CRM_CALENDAR_SETTINGS = gql`
    mutation SaveCrmCalendarSettings($input: SaveCrmCalendarSettingsInput!) {
        saveCrmCalendarSettings(input: $input) {
            dayStartHour
            dayEndHour
        }
    }
`;

/* Place a meeting on the calendar (POGO-13214 P4.2b) — creates the Google event
   on the chosen host's calendar and persists the booking. */
export const PLACE_CRM_MEETING = gql`
    mutation PlaceCrmMeeting($input: PlaceCrmMeetingInput!) {
        placeCrmMeeting(input: $input) {
            ${CRM_CALENDAR_BOOKING_FIELDS}
        }
    }
`;
